import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import LogoText from './LogoText';

const HeaderContainer = styled.header`
  padding: var(--spacing-md) 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  
  &.scrolled {
    background-color: var(--color-background);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-family: var(--font-primary);
  font-weight: var(--font-weight-light);
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
`;

const SecondaryLogo = styled.img`
  height: 45px;
  width: auto;
  margin-left: 15px;
`;



const MenuLinks = styled.div`
  display: flex;
  gap: var(--spacing-md);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  padding: 0.5rem 0;
  opacity: ${props => props.$active ? 1 : 0.7};
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: ${props => props.$active ? '100%' : '0'};
    height: 1px;
    background-color: var(--color-text);
    transition: width 0.3s ease;
  }
  
  &:hover {
    opacity: 1;
    
    &::after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  display: none;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 300px;
  background-color: var(--color-background);
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  padding: var(--spacing-xl) var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  z-index: 200;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
`;

const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: ${props => props.$active ? 1 : 0.7};
`;

const CloseButton = styled.button`
  align-self: flex-end;
  background: none;
  border: none;
  font-size: 1.5rem;
  padding: 0.5rem;
  cursor: pointer;
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 150;
`;

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  
  return (
    <HeaderContainer className={isScrolled ? 'scrolled' : ''}>
      <div className="container">
        <Nav>
          <Logo to="/" aria-label="Accueil - Fabienne Boé de Pirey">
            <picture>
              <source srcSet="/images/logo.svg" type="image/svg+xml" />
              <LogoImage src="/images/logo.svg" alt="Fabienne Boé de Pirey" onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.removeAttribute('style');
              }} />
              <span style={{ display: 'none' }}>
                <LogoText />
              </span>
            </picture>
            <SecondaryLogo 
              src="https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo/logofbdep.png" 
              alt="Logo FBDP" 
            />
          </Logo>
          
          <MenuLinks>
            <NavLink to="/" $active={location.pathname === '/'}>Accueil</NavLink>
            <NavLink to="/services" $active={location.pathname === '/services'}>Présentation</NavLink>
            <NavLink to="/galerie" $active={location.pathname.startsWith('/galerie')}>Galerie</NavLink>
            <NavLink to="/details" $active={location.pathname === '/details'}>Détails</NavLink>
            <NavLink to="/retombees-presse" $active={location.pathname === '/retombees-presse'}>Retombées Presse</NavLink>
            <NavLink to="/about" $active={location.pathname === '/about'}>A propos</NavLink>
            <NavLink to="/contact" $active={location.pathname === '/contact'}>Contact</NavLink>
          </MenuLinks>
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12H21" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 6H21" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 18H21" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </MobileMenuButton>
        </Nav>
      </div>
      
      <Overlay $isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
      
      <MobileMenu $isOpen={isMobileMenuOpen}>
        <CloseButton onClick={closeMobileMenu}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>
        
                <MobileNavLink to="/" onClick={closeMobileMenu} $active={location.pathname === '/'}>Accueil</MobileNavLink>
        <MobileNavLink to="/services" onClick={closeMobileMenu} $active={location.pathname === '/services'}>Présentation</MobileNavLink>
        <MobileNavLink to="/galerie" onClick={closeMobileMenu} $active={location.pathname.startsWith('/galerie')}>Galerie</MobileNavLink>
        <MobileNavLink to="/details" onClick={closeMobileMenu} $active={location.pathname === '/details'}>Détails</MobileNavLink>
        <MobileNavLink to="/retombees-presse" onClick={closeMobileMenu} $active={location.pathname === '/retombees-presse'}>Retombées Presse</MobileNavLink>
        <MobileNavLink to="/about" onClick={closeMobileMenu} $active={location.pathname === '/about'}>A propos</MobileNavLink>
        <MobileNavLink to="/contact" onClick={closeMobileMenu} $active={location.pathname === '/contact'}>Contact</MobileNavLink>
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;
